import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import * as Sentry from '@sentry/react';
export const INTERCOM_HELP_CENTER_URL = 'https://intercom.help/anchor-billing/en/';
const THIRD_PARTY_PROBE_URL = 'https://www.google-analytics.com/analytics.js';
const PROBE_TIMEOUT_MS = 3000;
const PRIVATE_BROWSING_PROBE_TIMEOUT_MS = 100;
const INCOGNITO_QUOTA_CEILING_BYTES = 130 * 1024 * 1024;
export const MAX_RECORDED_CSP_VIOLATIONS = 20;
const UNREPORTABLE_INTERCOM_LOAD_CAUSES = new Set([
    'script_execution_blocked',
    'ad_blocker_or_privacy_extension',
    'intercom_specifically_blocked',
    'csp_violation',
    'network_timeout',
    'offline'
]);
export const shouldReportIntercomLoadFailure = (cause, reportedCauses)=>{
    if (UNREPORTABLE_INTERCOM_LOAD_CAUSES.has(cause)) {
        return false;
    }
    return !reportedCauses.has(cause);
};
export const classifyIntercomLoadFailure = (param)=>{
    let { isOnline, cspViolations, intercomFetchResult, thirdPartyFetchResult } = param;
    const intercomBlocked = intercomFetchResult.startsWith('failed');
    const thirdPartyBlocked = thirdPartyFetchResult.startsWith('failed');
    if (!isOnline) {
        return 'offline';
    }
    if (cspViolations.length > 0) {
        return 'csp_violation';
    }
    if (intercomBlocked && thirdPartyBlocked) {
        return 'ad_blocker_or_privacy_extension';
    }
    if (intercomBlocked) {
        return 'intercom_specifically_blocked';
    }
    if (intercomFetchResult === 'success') {
        return 'script_execution_blocked';
    }
    if (intercomFetchResult === 'timeout') {
        return 'network_timeout';
    }
    return 'unknown';
};
export const consoleMessageForIntercomLoadCause = (cause, isPrivateBrowsing)=>{
    const baseMessage = '[Anchor Support Chat] Failed to load Intercom.';
    const privateBrowsingNote = isPrivateBrowsing ? ' Note: You appear to be using private/incognito browsing mode, which may have stricter privacy settings.' : '';
    switch(cause){
        case 'offline':
            return "".concat(baseMessage, " You appear to be offline. Please check your internet connection and refresh the page.");
        case 'ad_blocker_or_privacy_extension':
            return "".concat(baseMessage, " This is likely caused by an ad blocker or privacy extension.").concat(privateBrowsingNote, ' To enable the Anchor Support chat, please add an exception for "intercom.io" in your ad blocker settings.');
        case 'intercom_specifically_blocked':
            return "".concat(baseMessage, " Intercom appears to be specifically blocked. This could be due to a browser extension, firewall, or corporate network policy.").concat(privateBrowsingNote, ' To enable the Anchor Support chat, please whitelist "intercom.io" or contact your IT administrator.');
        case 'script_execution_blocked':
            return "".concat(baseMessage, " The script was blocked from executing. This could be caused by a browser privacy setting, security extension, or firewall.").concat(privateBrowsingNote, ' To enable the Anchor Support chat, please check your browser\'s security settings or whitelist "intercom.io".');
        case 'csp_violation':
            return "".concat(baseMessage, " A Content Security Policy violation occurred. This may be caused by a browser extension modifying security headers.").concat(privateBrowsingNote, " To enable the Anchor Support chat, please disable extensions that modify CSP headers.");
        case 'network_timeout':
            return "".concat(baseMessage, " The request timed out. This could be due to slow network connectivity or a firewall blocking the connection.").concat(privateBrowsingNote, " Please check your network and try refreshing the page.");
        default:
            return "".concat(baseMessage, " The cause is unknown.").concat(privateBrowsingNote, " If you need support, please visit ").concat(INTERCOM_HELP_CENTER_URL);
    }
};
export const toScriptLoadError = (error)=>{
    const errorEvent = error;
    return {
        message: errorEvent.message || errorEvent.type || 'script load error',
        type: errorEvent.type || 'error',
        filename: errorEvent.filename,
        lineno: errorEvent.lineno,
        colno: errorEvent.colno,
        raw: JSON.stringify(errorEvent)
    };
};
export const collectIntercomDiagnostics = async (probe, scriptUrl, scriptLoadError)=>{
    try {
        const [intercomFetchResult, thirdPartyFetchResult, isPrivateBrowsing] = await Promise.all([
            probe.probeUrl(scriptUrl),
            probe.probeUrl(THIRD_PARTY_PROBE_URL),
            probe.detectPrivateBrowsing()
        ]);
        const signals = {
            isOnline: probe.isOnline(),
            cspViolations: probe.cspViolations(),
            intercomFetchResult,
            thirdPartyFetchResult
        };
        return _object_spread_props(_object_spread({}, signals), {
            likelyCause: classifyIntercomLoadFailure(signals),
            isPrivateBrowsing,
            scriptLoadError
        });
    } catch (diagnosticError) {
        Sentry.captureException(diagnosticError, {
            tags: {
                context: 'intercom_diagnostics_collection_failed'
            }
        });
        return {
            likelyCause: 'unknown',
            intercomFetchResult: 'unknown',
            thirdPartyFetchResult: 'unknown',
            cspViolations: [],
            isOnline: probe.isOnline(),
            isPrivateBrowsing: null,
            scriptLoadError
        };
    }
};
const probeUrlOverNetwork = async (url)=>{
    const controller = new AbortController();
    const timeoutId = setTimeout(()=>controller.abort(), PROBE_TIMEOUT_MS);
    try {
        await fetch(url, {
            method: 'HEAD',
            mode: 'no-cors',
            signal: controller.signal
        });
        return 'success';
    } catch (error) {
        if (error instanceof Error) {
            return error.name === 'AbortError' ? 'timeout' : "failed: ".concat(error.message);
        }
        return 'failed: unknown';
    } finally{
        clearTimeout(timeoutId);
    }
};
const probeIndexedDbAvailability = ()=>new Promise((resolve)=>{
        const testDb = 'intercom-private-test';
        const request = indexedDB.open(testDb);
        const timeoutId = setTimeout(()=>resolve(null), PRIVATE_BROWSING_PROBE_TIMEOUT_MS);
        const settle = (isPrivate)=>{
            clearTimeout(timeoutId);
            resolve(isPrivate);
        };
        request.onerror = ()=>settle(true);
        request.onsuccess = ()=>{
            request.result.close();
            indexedDB.deleteDatabase(testDb);
            settle(false);
        };
    });
const detectPrivateBrowsingInBrowser = async ()=>{
    try {
        var _navigator_storage;
        if (((_navigator_storage = navigator.storage) === null || _navigator_storage === void 0 ? void 0 : _navigator_storage.estimate) != null) {
            const { quota } = await navigator.storage.estimate();
            if (quota != null && quota < INCOGNITO_QUOTA_CEILING_BYTES) {
                return true;
            }
        }
        return await probeIndexedDbAvailability();
    } catch (unused) {
        return null;
    }
};
export const makeBrowserDiagnosticsProbe = ()=>{
    const violations = [];
    document.addEventListener('securitypolicyviolation', (event)=>{
        var _event_blockedURI;
        if (((_event_blockedURI = event.blockedURI) === null || _event_blockedURI === void 0 ? void 0 : _event_blockedURI.includes('intercom')) && violations.length < MAX_RECORDED_CSP_VIOLATIONS) {
            violations.push("".concat(event.violatedDirective, ": ").concat(event.blockedURI));
        }
    });
    return {
        isOnline: ()=>navigator.onLine,
        probeUrl: probeUrlOverNetwork,
        detectPrivateBrowsing: detectPrivateBrowsingInBrowser,
        cspViolations: ()=>violations
    };
};
